import { useCallback, useEffect, useMemo, useState } from "react";
import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import { Loader2, RefreshCw, ShieldAlert, Heart } from "lucide-react";
import { toast } from "sonner";

import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";

type EnrollmentStatus = "pending" | "under_review" | "approved" | "rejected" | "waitlisted";

type EnrollmentRow = {
  id: string;
  full_name: string;
  email: string;
  phone: string | null;
  city_state: string | null;
  household_size: number | null;
  monthly_income_cents: number | null;
  income_source: string | null;
  requested_monthly_cents: number;
  hardship_description: string;
  services_requested: string[];
  documentation_notes: string | null;
  consent_income_review: boolean;
  consent_advocacy_only: boolean;
  status: EnrollmentStatus;
  approved_monthly_cents: number | null;
  approval_expires_at: string | null;
  reviewer_notes: string | null;
  reviewed_at: string | null;
  created_at: string;
};

const STATUS_TONE: Record<EnrollmentStatus, string> = {
  pending: "bg-amber-500/15 text-amber-600 border-amber-500/30",
  under_review: "bg-sky-500/15 text-sky-600 border-sky-500/30",
  approved: "bg-emerald-500/15 text-emerald-600 border-emerald-500/30",
  rejected: "bg-destructive/15 text-destructive border-destructive/30",
  waitlisted: "bg-muted text-muted-foreground border-border",
};

const money = (cents: number | null) =>
  cents === null || cents === undefined
    ? "—"
    : (cents / 100).toLocaleString(undefined, { style: "currency", currency: "USD" });

const fmt = (value: string | null) =>
  value ? new Date(value).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" }) : "—";

const defaultExpiry = () => {
  const d = new Date();
  d.setMonth(d.getMonth() + 3);
  return d.toISOString().slice(0, 10);
};

const ReviewCard = ({
  row,
  busy,
  onAct,
}: {
  row: EnrollmentRow;
  busy: boolean;
  onAct: (
    next: EnrollmentStatus,
    payload: { approvedCents: number; expiresAt: string; notes: string },
  ) => void;
}) => {
  const [approvedDollars, setApprovedDollars] = useState(
    String((row.approved_monthly_cents ?? row.requested_monthly_cents) / 100),
  );
  const [expiresAt, setExpiresAt] = useState(row.approval_expires_at ?? defaultExpiry());
  const [notes, setNotes] = useState("");

  const act = (next: EnrollmentStatus) => {
    const cents = Math.round(Number(approvedDollars) * 100);
    if (next === "approved" && (!Number.isFinite(cents) || cents < 9900 || cents > 12900)) {
      toast.error("Approved rate must be between $99 and $129.");
      return;
    }
    onAct(next, { approvedCents: cents, expiresAt, notes: notes.trim() });
  };

  return (
    <Card>
      <CardHeader className="flex flex-row items-start justify-between gap-4 space-y-0">
        <div>
          <CardTitle className="text-base">{row.full_name}</CardTitle>
          <p className="text-sm text-muted-foreground">
            {[row.email, row.phone, row.city_state].filter(Boolean).join(" · ")}
          </p>
        </div>
        <Badge variant="outline" className={STATUS_TONE[row.status]}>
          {row.status.replace("_", " ")}
        </Badge>
      </CardHeader>
      <CardContent className="space-y-4 text-sm">
        <div className="grid gap-2 sm:grid-cols-3">
          {[
            { label: "Submitted", value: fmt(row.created_at) },
            { label: "Requested rate", value: `${money(row.requested_monthly_cents)}/mo` },
            { label: "Household size", value: row.household_size?.toString() ?? "—" },
            { label: "Monthly income", value: money(row.monthly_income_cents) },
            { label: "Income source", value: row.income_source ?? "—" },
            { label: "Last reviewed", value: fmt(row.reviewed_at) },
          ].map((m) => (
            <div key={m.label}>
              <div className="text-[11px] uppercase tracking-wide text-muted-foreground">{m.label}</div>
              <div>{m.value}</div>
            </div>
          ))}
        </div>

        <div>
          <div className="text-[11px] uppercase tracking-wide text-muted-foreground">Hardship</div>
          <p className="whitespace-pre-wrap">{row.hardship_description}</p>
        </div>

        <div className="flex flex-wrap gap-1.5">
          {row.services_requested.map((s) => (
            <span key={s} className="rounded-full border bg-muted px-2 py-0.5 text-[11px]">
              {s}
            </span>
          ))}
        </div>

        {row.documentation_notes ? (
          <div>
            <div className="text-[11px] uppercase tracking-wide text-muted-foreground">
              Documentation offered
            </div>
            <p className="whitespace-pre-wrap">{row.documentation_notes}</p>
          </div>
        ) : null}

        {row.status === "approved" ? (
          <p className="rounded-md border border-emerald-500/30 bg-emerald-500/10 p-3 text-xs text-emerald-700">
            Approved at {money(row.approved_monthly_cents)}/mo · valid through{" "}
            {row.approval_expires_at ?? "—"}. This email can now start the monthly plan at checkout.
          </p>
        ) : null}

        {row.reviewer_notes ? (
          <p className="text-xs text-muted-foreground">Notes: {row.reviewer_notes}</p>
        ) : null}

        <div className="grid gap-3 sm:grid-cols-2">
          <div>
            <Label htmlFor={`rate-${row.id}`} className="text-xs">
              Approved monthly rate ($99–$129)
            </Label>
            <Input
              id={`rate-${row.id}`}
              type="number"
              min={99}
              max={129}
              step="1"
              value={approvedDollars}
              onChange={(e) => setApprovedDollars(e.target.value)}
            />
          </div>
          <div>
            <Label htmlFor={`exp-${row.id}`} className="text-xs">
              Approval valid through
            </Label>
            <Input
              id={`exp-${row.id}`}
              type="date"
              value={expiresAt}
              onChange={(e) => setExpiresAt(e.target.value)}
            />
          </div>
        </div>

        <div>
          <Label htmlFor={`notes-${row.id}`} className="text-xs">
            Reviewer notes
          </Label>
          <Textarea
            id={`notes-${row.id}`}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Documentation received, scope agreed, follow-up needed…"
          />
        </div>

        <div className="flex flex-wrap gap-2">
          <Button size="sm" disabled={busy} onClick={() => act("approved")}>
            {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
            Approve
          </Button>
          <Button size="sm" variant="outline" disabled={busy} onClick={() => act("under_review")}>
            Mark under review
          </Button>
          <Button size="sm" variant="outline" disabled={busy} onClick={() => act("waitlisted")}>
            Waitlist
          </Button>
          <Button size="sm" variant="destructive" disabled={busy} onClick={() => act("rejected")}>
            Reject
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

const AdminCompassion = () => {
  const { user, loading: authLoading } = useAuth();
  const [isAdmin, setIsAdmin] = useState<boolean | null>(null);
  const [rows, setRows] = useState<EnrollmentRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [showResolved, setShowResolved] = useState(false);

  useEffect(() => {
    if (authLoading) return;
    if (!user) {
      setIsAdmin(false);
      return;
    }
    supabase
      .rpc("has_role", { _user_id: user.id, _role: "admin" })
      .then(({ data, error }) => setIsAdmin(!error && data === true));
  }, [authLoading, user]);

  const load = useCallback(async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from("compassion_enrollments")
      .select("*")
      .order("created_at", { ascending: false });
    if (error) toast.error(error.message);
    setRows((data as EnrollmentRow[]) ?? []);
    setLoading(false);
  }, []);

  useEffect(() => {
    if (isAdmin) void load();
  }, [isAdmin, load]);

  const visible = useMemo(
    () =>
      rows.filter(
        (r) => showResolved || r.status === "pending" || r.status === "under_review",
      ),
    [rows, showResolved],
  );
  const pendingCount = rows.filter((r) => r.status === "pending").length;

  const act = async (
    row: EnrollmentRow,
    next: EnrollmentStatus,
    payload: { approvedCents: number; expiresAt: string; notes: string },
  ) => {
    setBusyId(row.id);
    const { error } = await supabase.rpc("set_compassion_enrollment_status", {
      _id: row.id,
      _status: next,
      _approved_monthly_cents: next === "approved" ? payload.approvedCents : null,
      _approval_expires_at: next === "approved" ? payload.expiresAt || null : null,
      _notes: payload.notes ? payload.notes : null,
    });
    setBusyId(null);
    if (error) {
      toast.error(error.message);
      return;
    }
    toast.success(`Enrollment ${next.replace("_", " ")}`);
    void load();
  };

  if (authLoading || isAdmin === null) {
    return (
      <main className="flex min-h-screen items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </main>
    );
  }

  if (!isAdmin) {
    return (
      <main className="mx-auto flex min-h-screen max-w-md flex-col items-center justify-center gap-4 px-6 text-center">
        <ShieldAlert className="h-8 w-8 text-muted-foreground" />
        <h1 className="text-xl font-semibold">Admin access required</h1>
        <p className="text-sm text-muted-foreground">
          Sign in with an administrator account to review Compassion Plan enrollments.
        </p>
        <Button asChild>
          <Link to="/auth">Sign in</Link>
        </Button>
      </main>
    );
  }

  return (
    <main className="mx-auto max-w-4xl px-4 py-10">
      <Helmet>
        <title>Compassion Plan Review | Admin</title>
        <meta
          name="description"
          content="Internal admin view to review and approve Compassion Plan enrollment requests."
        />
        <meta name="robots" content="noindex,nofollow" />
      </Helmet>

      <header className="mb-6 flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="flex items-center gap-2 text-2xl font-semibold">
            <Heart className="h-5 w-5" /> Compassion Plan review
          </h1>
          <p className="text-sm text-muted-foreground">
            {pendingCount} pending {pendingCount === 1 ? "request" : "requests"}. Approval unlocks
            monthly checkout at the approved rate.
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={() => setShowResolved((v) => !v)}>
            {showResolved ? "Hide resolved" : "Show resolved"}
          </Button>
          <Button variant="outline" size="sm" onClick={() => void load()} disabled={loading}>
            {loading ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <RefreshCw className="mr-2 h-4 w-4" />
            )}
            Refresh
          </Button>
        </div>
      </header>

      <div className="space-y-4">
        {visible.length === 0 && (
          <p className="text-sm text-muted-foreground">Nothing to review here.</p>
        )}
        {visible.map((row) => (
          <ReviewCard
            key={row.id}
            row={row}
            busy={busyId === row.id}
            onAct={(next, payload) => void act(row, next, payload)}
          />
        ))}
      </div>
    </main>
  );
};

export default AdminCompassion;
